import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';

import { Text } from '@components/common';
import { ExportCsvButton } from '@components/common/ExportCsvButton';
import { SortableTree } from '@components/common/SortableTree';
import { getMaxDepthCount } from '@components/common/SortableTree/utils';
import * as Models from '@components/SurveyBuilder/types/models';
import { Tooltip } from '@components/Tooltip';
import { track } from '@components/tracking';
import { usePermission } from '@hooks/usePermission';

import { findNodeDeep, getCsvDataForResultsTable, parseItems } from '../utils';

import { ResultsTableItem } from './ResultsTableItem';

interface Props {
  answers: NonNullable<ScreenerResponseAnswerValue<'tree_test'>>[];
  showPercentage: boolean;
  nodes: Models.TreeItem[];
  blockableId: number;
}

type ResultsTreeItem = Models.TreeItem & { count: number; directCount: number };

export const ResultsTable: React.FC<React.PropsWithChildren<Props>> = ({
  answers,
  nodes,
  showPercentage,
  blockableId
}) => {
  const maxDepth = useMemo(() => getMaxDepthCount(nodes), [nodes]);

  const parsedItems = useMemo(() => parseItems(nodes, nodes, answers), [nodes, answers]);

  const [items, setItems] = useState<Models.TreeItem[]>(parsedItems);

  const isAdmin = usePermission('isAdmin')();

  const resultsFilename = `tree_test_results_${blockableId}_results_table_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.csv`;

  const resultsCsvData = useMemo(() => {
    return getCsvDataForResultsTable(nodes, answers, maxDepth, showPercentage);
  }, [nodes, answers, maxDepth, showPercentage]);

  useEffect(() => {
    setItems(parsedItems);
  }, [parsedItems]);

  const renderItem = useCallback(
    (props: any) => {
      const currentItem = findNodeDeep<ResultsTreeItem>(parsedItems, props.id);

      return (
        <ResultsTableItem
          {...props}
          maxDepth={maxDepth}
          showPercentage={showPercentage}
          answers={answers}
          directCount={currentItem?.directCount}
          count={currentItem?.count}
        />
      );
    },

    [nodes, answers, showPercentage, maxDepth]
  );

  return (
    <div className='w-full'>
      <div className='mb-1 flex h-8 items-center justify-between'>
        <div className='flex items-center justify-between'>
          <Text bold>Results</Text>
        </div>

        <Tooltip content='Export results to CSV' isDisabled={!isAdmin}>
          <ExportCsvButton
            onClick={() => track('export_overall_metrics_csv_button_clicked', { type: 'tree_test', tab: 'result' })}
            label=''
            aria-label='Export results to CSV'
            data-testid='export-table-to-csv'
            disabledTooltip='Only admin users may export Survey results'
            medium
            disabled={!isAdmin}
            data={resultsCsvData.data}
            headers={resultsCsvData.headers}
            filename={resultsFilename}
          />
        </Tooltip>
      </div>
      <div className='relative mt-4 max-w-full overflow-auto'>
        <div className='mb-4 flex w-full flex-shrink-0 items-center justify-start'>
          <div className='h400 w-100 flex-shrink-0 font-bold' style={{ marginRight: maxDepth * 16 }}>
            Tree structure
          </div>
          <div className='flex flex-nowrap items-center space-x-8'>
            <Text h='400' className='w-44' bold>
              Result
            </Text>
            <Text h='400' className='w-44' bold>
              Direct
            </Text>
          </div>
        </div>

        <SortableTree collapsible setItems={setItems} renderItem={renderItem} indentationWidth={16} items={items} />
      </div>
    </div>
  );
};
